import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, MessageCircle, Mail, Phone, MapPin } from "lucide-react";
import { subscribeNewsletter } from "../lib/api";
import { toast } from "sonner";
import { WA_CATALOG_URL, WA_PHONE_INTL, INSTAGRAM_URL, INSTAGRAM_HANDLE } from "./WhatsAppButton";

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await subscribeNewsletter(email);
      toast.success(res.message);
      setEmail("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <footer className="bg-brand-maroon-deep text-brand-ivory mt-20">
      {/* Newsletter */}
      <div className="border-b border-brand-gold/25">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="label-tiny text-brand-gold mb-3">Join The Bindni Circle</p>
            <h3 className="font-serif-brand text-3xl lg:text-4xl leading-tight">Unlock 10% off your first Bindni</h3>
            <p className="text-brand-ivory/70 mt-3 text-sm">Early access to drops, private sales, and behind-the-scenes stories in your inbox.</p>
          </div>
          <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3" data-testid="newsletter-form">
            <input
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email address"
              className="flex-1 bg-transparent border border-brand-gold/40 px-5 py-4 outline-none text-brand-ivory placeholder:text-brand-ivory/50 focus:border-brand-gold"
              data-testid="newsletter-email-input"
            />
            <button disabled={loading} className="btn-outline-gold border-brand-gold text-brand-ivory hover:bg-brand-gold hover:text-brand-charcoal" data-testid="newsletter-submit-btn">
              {loading ? "Subscribing…" : "Subscribe"}
            </button>
          </form>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 grid grid-cols-2 lg:grid-cols-5 gap-10">
        <div className="col-span-2">
          <span className="font-serif-brand text-3xl">Bindni Jewels</span>
          <p className="label-tiny text-brand-gold mt-2">Royal · Handcrafted · Anti-Tarnish · Since 2026</p>
          <p className="text-sm text-brand-ivory/70 mt-5 max-w-sm leading-relaxed">
            Everyday-luxe jewellery for modern women. Heirloom-inspired, anti-tarnish, and made for real life — from weekday meetings to Saturday sangeets.
          </p>
          <div className="flex gap-4 mt-6">
            <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" data-testid="footer-instagram" className="border border-brand-gold/30 p-3 hover:bg-brand-gold hover:text-brand-charcoal transition-colors duration-300"><Instagram size={18} strokeWidth={1.5} /></a>
            <a href="https://facebook.com" target="_blank" rel="noreferrer" data-testid="footer-facebook" className="border border-brand-gold/30 p-3 hover:bg-brand-gold hover:text-brand-charcoal transition-colors duration-300"><Facebook size={18} strokeWidth={1.5} /></a>
            <a href={WA_CATALOG_URL} target="_blank" rel="noreferrer" data-testid="footer-whatsapp" className="border border-brand-gold/30 p-3 hover:bg-brand-gold hover:text-brand-charcoal transition-colors duration-300" title="WhatsApp Catalogue"><MessageCircle size={18} strokeWidth={1.5} /></a>
          </div>
        </div>

        <div>
          <p className="label-tiny text-brand-gold mb-5">Shop</p>
          <ul className="space-y-3 text-sm text-brand-ivory/80">
            <li><Link to="/shop?collection=rajputana" className="hover:text-brand-gold">Rajputana</Link></li>
            <li><Link to="/shop?collection=bridal" className="hover:text-brand-gold">Bridal</Link></li>
            <li><Link to="/shop?category=necklaces" className="hover:text-brand-gold">Necklaces</Link></li>
            <li><Link to="/shop?category=earrings" className="hover:text-brand-gold">Earrings</Link></li>
            <li><Link to="/shop?category=bangles" className="hover:text-brand-gold">Bangles</Link></li>
            <li><Link to="/shop?category=rings" className="hover:text-brand-gold">Rings</Link></li>
          </ul>
        </div>

        <div>
          <p className="label-tiny text-brand-gold mb-5">Help</p>
          <ul className="space-y-3 text-sm text-brand-ivory/80">
            <li><Link to="/faq" className="hover:text-brand-gold">FAQ</Link></li>
            <li><Link to="/track-order" className="hover:text-brand-gold">Track Order</Link></li>
            <li><Link to="/shipping-policy" className="hover:text-brand-gold">Shipping Policy</Link></li>
            <li><Link to="/return-policy" className="hover:text-brand-gold">Returns · 15 Days</Link></li>
            <li><Link to="/privacy-policy" className="hover:text-brand-gold">Privacy Policy</Link></li>
            <li><Link to="/terms" className="hover:text-brand-gold">Terms of Service</Link></li>
            <li><Link to="/contact" className="hover:text-brand-gold">Contact</Link></li>
          </ul>
        </div>

        <div>
          <p className="label-tiny text-brand-gold mb-5">Reach Us</p>
          <ul className="space-y-3 text-sm text-brand-ivory/80">
            <li className="flex gap-2 items-start"><Mail size={14} className="mt-1 shrink-0" /> care@bindnijewels.com</li>
            <li className="flex gap-2 items-start"><Phone size={14} className="mt-1 shrink-0" /> {WA_PHONE_INTL}</li>
            <li className="flex gap-2 items-start"><MessageCircle size={14} className="mt-1 shrink-0" /> <a href={WA_CATALOG_URL} target="_blank" rel="noreferrer" className="hover:text-brand-gold underline-offset-2 hover:underline">WhatsApp Catalogue</a></li>
            <li className="flex gap-2 items-start"><Instagram size={14} className="mt-1 shrink-0" /> <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" className="hover:text-brand-gold">{INSTAGRAM_HANDLE}</a></li>
            <li className="flex gap-2 items-start"><MapPin size={14} className="mt-1 shrink-0" /> 10 B, Pushp Vihar Colony Extension,<br/>Indore, Madhya Pradesh 452010, India</li>
          </ul>
        </div>
      </div>

      <div className="border-t border-brand-gold/20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-brand-ivory/60">
          <p>© {new Date().getFullYear()} Bindni Jewels. All rights reserved. Made in India · Made with Love.</p>
          <div className="flex flex-wrap gap-4 lg:gap-6"><Link to="/privacy-policy" className="hover:text-brand-gold">Privacy Policy</Link><Link to="/terms" className="hover:text-brand-gold">Terms of Service</Link><Link to="/shipping-policy" className="hover:text-brand-gold">Shipping</Link><Link to="/return-policy" className="hover:text-brand-gold">Returns · 15 Days</Link><span>All prices in INR</span></div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
